import { Cake, LocationCity, MonetizationOn, Person, Phone, Work } from '@mui/icons-material';
import LocationOnIcon from '@mui/icons-material/LocationOnOutlined';
import { Box, Button, Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CustomMDPreview from '../../components/CustomMD/CustomMDPreview';
import UserImage from '../../components/UserImage';
import User from '../../types/User';
import { getNullableString } from '../../util/string';
import { AboutMe, Education, PortfolioCard, Skills, WorkHistory } from '../Profile/components';

type UserDetailsProps = {
    user: User | null;
}

const UserDetails = ({ user }: UserDetailsProps) => {
    const navigate = useNavigate();

    if (!user)
        return (
            <Card>
                <CardContent>
                    <Typography>No user details available.</Typography>
                </CardContent>
            </Card>
        );

    const handleToProfile = () => {
        navigate(`/profile/${user._id}`);
    }

    return (
        <Card>
            <CardContent>
                <Stack gap={3} pb={4}>
                    <Stack direction="row" gap={2} justifyContent="space-between" >
                        <Stack direction="row" gap={2} alignItems="center">
                            <UserImage image={user.image} size={100} name={user.fullName || user.email} />
                            <Stack gap={1}>
                                <Typography variant="h4">{getNullableString(user.fullName || user.email)}</Typography>
                                <Typography variant="subtitle2" color="text.secondary" fontWeight={400}>{getNullableString(user.email)}</Typography>
                            </Stack>
                        </Stack>

                        <Box>
                            <Button variant="contained" color="primary" onClick={handleToProfile}>
                                Go to Profile
                            </Button>
                        </Box>
                    </Stack>

                    <Stack alignItems="start" gap={1.5}>
                        <Typography variant="h6">Personal Information</Typography>

                        {/* First row - 2 columns */}
                        <Stack direction="row" spacing={4} width="100%">
                            {/* Left column */}
                            <Stack direction="row" gap={1} alignItems="center" flex={1}>
                                <LocationOnIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
                                <Typography variant="body1" fontWeight="light">{getNullableString(user.location, "Not set yet")}</Typography>
                            </Stack>

                            {/* Right column */}
                            <Stack direction="row" gap={1} alignItems="center" flex={1}>
                                <Phone fontSize="inherit" sx={{ color: 'text.secondary' }} />
                                <Typography variant="body1" fontWeight="light">{getNullableString(user.phone, "Not set yet")}</Typography>
                            </Stack>
                        </Stack>

                        {/* Second row - 2 columns */}
                        <Stack direction="row" spacing={4} width="100%">
                            {/* Left column */}
                            <Stack direction="row" gap={1} alignItems="center" flex={1}>
                                <Cake fontSize="inherit" sx={{ color: 'text.secondary' }} />
                                <Typography variant="body1" fontWeight="light">
                                    {user.birthDate ? new Date(user.birthDate).toLocaleDateString() : "Not set yet"}
                                </Typography>
                            </Stack>

                            {/* Right column */}
                            <Stack direction="row" gap={1} alignItems="center" flex={1}>
                                <Person fontSize="inherit" sx={{ color: 'text.secondary' }} />
                                <Typography variant="body1" fontWeight="light">{getNullableString(user.gender, "Not set yet")}</Typography>
                            </Stack>
                        </Stack>
                    </Stack>

                    <Divider />

                    {user.jobTitle && (
                        <>
                            <Stack gap={1}>
                                <Typography variant="h6">{getNullableString(user.jobTitle, "Job Title Not set yet")}</Typography>
                                <CustomMDPreview description={getNullableString(user.jobDescription)} />
                            </Stack>
                            <Divider />
                        </>
                    )}

                    {(user.jobLookingFor || user.jobLookingForType || user.jobLookingForLocation || user.jobLookingForSalary) && (
                        <>
                            <Stack gap={1}>
                                <Typography variant="h6">Looking For</Typography>
                                {user.jobLookingFor && (
                                    <CustomMDPreview description={getNullableString(user.jobLookingFor)} />
                                )}
                                <Stack gap={1}>
                                    {user.jobLookingForType && (
                                        <Stack direction="row" gap={1} alignItems="center">
                                            <Work fontSize="inherit" sx={{ color: 'text.secondary' }} />
                                            <Typography variant="body1" fontWeight="light">Type: {getNullableString(user.jobLookingForType)}</Typography>
                                        </Stack>
                                    )}
                                    {user.jobLookingForLocation && (
                                        <Stack direction="row" gap={1} alignItems="center">
                                            <LocationCity fontSize="inherit" sx={{ color: 'text.secondary' }} />
                                            <Typography variant="body1" fontWeight="light">Location: {getNullableString(user.jobLookingForLocation)}</Typography>
                                        </Stack>
                                    )}
                                    {user.jobLookingForSalary && (
                                        <Stack direction="row" gap={1} alignItems="center">
                                            <MonetizationOn fontSize="inherit" sx={{ color: 'text.secondary' }} />
                                            <Typography variant="body1" fontWeight="light">
                                                Expected Salary: ${user.jobLookingForSalary.toLocaleString()}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>
                            </Stack>
                            <Divider />
                        </>
                    )}

                    <AboutMe
                        userData={user}
                        setUserData={() => { }}
                        editMode={false}
                        paperProps={{ sx: { p: 0, boxShadow: "none" } }}
                    />

                    <Divider />

                    <PortfolioCard
                        userId={user._id}
                        editMode={false}
                        paperProps={{ sx: { p: 0, boxShadow: "none" } }}
                        hideDraft={true}
                        disabledEdit={true}
                    />
                    <Divider />

                    <Skills
                        skills={user.skills?.split(',') || []}
                        setSkills={() => { }}
                        editMode={false}
                        paperProps={{ sx: { p: 0, boxShadow: "none" } }}
                    />
                    <Divider />

                    <Education
                        userData={user}
                        setUserData={() => { }}
                        editMode={false}
                        paperProps={{ sx: { p: 0, boxShadow: "none" } }}
                    />
                    <Divider />

                    <WorkHistory
                        userData={user}
                        setUserData={() => { }}
                        editMode={false}
                        paperProps={{ sx: { p: 0, boxShadow: "none" } }}
                    />
                </Stack>
            </CardContent>
        </Card>
    );
}

export default UserDetails;